// @flow
import { buildSearchQuery, isBuyNowQuery } from '../../../utils/searchQuery';
import { fetchItemFacetsIfNeeded, getItemFacets } from '../../../redux/modules/itemFacets';
import { fetchItemsIfNeeded, getItem } from '../../../redux/modules/item';
import { fetchSearchFacetsIfNeeded, getRawSearchFacets } from '../../../redux/modules/searchFacets';
import { fetchSearchSuggestions, getSecondarySearchSuggestions } from '../../../redux/modules/searchSuggestions';
import { fetchSimilarItemsV3IfNeeded, getSimilarItemIds } from '../../../redux/modules/similarItemsV3';
import {
    getCategoryIdsFromQueryParams,
    getKeywordFromQueryParams,
    isArchivedSearch,
    parseQueryParams,
} from '../../../utils/queryParams';
import { getPromotedSearchResults, searchPromoted } from '../../../redux/modules/searchPromoted';
import { getSearchedItemIds, getSearchUiState, submitSearch } from '../../../redux/modules/search';
import cleanSearchTerms from '../../../utils/cleanSearchTerms';
import type { DataLoaderParams } from '../../../types/DataLoaderParams';
import { fetchCatalogAdIfNeeded } from '../../../redux/modules/catalogAd';
import { fetchItemBiddingIfNeeded } from '../../../redux/modules/biddingInfo';
import { fetchSellersIfNeeded } from '../../../redux/modules/seller';
import { getFacetIds } from '../../../utils/ItemPageUtils';
import { getSaveSearchParams } from '../../../redux/modules/saveSearch';
import { getSearchPageSize } from '../../../redux/modules/pagination';
import { isUserLoggedIn } from '../../../redux/modules/user';
import { recordAnalyticsForSearchItemsPageView } from '../../../redux/modules/analytics';
import { submitIsSavedSearchCheck } from '../../../redux/modules/saveSearchCheck';

export const DATA_LOADING_ID = 'results-plus-promoted';

export default ({ location, store }: DataLoaderParams) => {
    const { dispatch, getState } = store;
    const queryParams = parseQueryParams(location.search);
    const { pathname } = location;

    const archivedSearch = isArchivedSearch(queryParams, pathname);

    return Promise.all([dispatch(fetchSearchFacetsIfNeeded(archivedSearch))]).then(() => {
        const currentState = getState();
        const pageSize = getSearchPageSize(currentState);
        const rawFacets = getRawSearchFacets(currentState, archivedSearch);
        const searchQuery = buildSearchQuery({ pathname, queryParams, rawFacets, searchPageSize: pageSize });
        const isBIN = isBuyNowQuery(searchQuery);
        searchQuery.buyNow = !archivedSearch && isBIN;
        const { searchTerm } = searchQuery;
        const pageName = searchQuery.options.status.includes('sold') ? 'sold-search' : 'search';
        const keywords = getKeywordFromQueryParams(queryParams);
        const userLoggedIn = isUserLoggedIn(currentState);
        if (userLoggedIn) {
            const ssParams = getSaveSearchParams(queryParams);
            dispatch(submitIsSavedSearchCheck(ssParams));
        }

        return Promise.all([
            dispatch(submitSearch(searchQuery)),
            dispatch(searchPromoted({ pageName, searchQuery })),
            dispatch(fetchSearchSuggestions(cleanSearchTerms(searchTerm), true)),
        ]).then(() => {
            const searchState = getState();
            const itemIds = getSearchedItemIds(searchState);
            const promotedResults = getPromotedSearchResults(searchState);

            // If we have search results do all the stuff to get the data for them.
            if (itemIds.length) {
                const { houses } = getSecondarySearchSuggestions(searchState);

                const promises = [
                    dispatch(fetchItemsIfNeeded([...itemIds, ...promotedResults], DATA_LOADING_ID)),
                    dispatch(
                        fetchCatalogAdIfNeeded({
                            categoryIds: getCategoryIdsFromQueryParams(queryParams),
                            keywords,
                            pageName,
                        })
                    ),
                ];

                if (houses.length) {
                    promises.push(dispatch(fetchSellersIfNeeded([houses[0]?.houseId])));
                }

                return Promise.all(promises).then(() => {
                    return dispatch(recordAnalyticsForSearchItemsPageView());
                });

                // If we get no results back, we'll get the data for our zero results page
            } else {
                const { soldItemIds = [] } = getSearchUiState(searchState);
                const promises = [
                    dispatch(fetchItemsIfNeeded(soldItemIds, DATA_LOADING_ID)),
                    dispatch(fetchItemFacetsIfNeeded([soldItemIds[0]])),
                ];

                soldItemIds.forEach(id => promises.push(dispatch(fetchItemBiddingIfNeeded(id))));
                return Promise.all(promises).then(() => {
                    const state = getState();
                    const { itemId, sellerId, title } = getItem(state, soldItemIds[0]);
                    const facets = getItemFacets(state, itemId);
                    const categoryIds = getFacetIds(facets);
                    return Promise.all([
                        dispatch(
                            fetchSimilarItemsV3IfNeeded({
                                categoryIds,
                                itemId,
                                sellerId,
                                title,
                            })
                        ),
                        dispatch(recordAnalyticsForSearchItemsPageView()),
                    ]).then(() => {
                        const state = getState();
                        const similarItemIds = getSimilarItemIds(state, [itemId]);
                        return dispatch(fetchItemsIfNeeded(similarItemIds));
                    });
                });
            }
        });
    });
};
